import { useState, useEffect } from "react"
import { externalEntityService } from "../../../services/externalEntityService"
import { useAuth } from "../../../context/AuthContext"
import Alert from "../../common/Alert"
import { X, Plus, Pencil, Eye, Trash2 } from "lucide-react"

/**
 * ExternalShipmentPaymentModal
 *
 * Props:
 *   shipmentId      {string}   external_shipment_id
 *   shipment        {object}   full shipment object (for balance context)
 *   onClose         {fn}       called when modal is dismissed
 *   onPaymentSaved  {fn}       called after create/edit/delete so parent can refresh
 */
const ExternalShipmentPaymentModal = ({
  shipmentId,
  shipment,
  onClose,
  onPaymentSaved,
}) => {
  const { user } = useAuth()

  // ── view states: "list" | "view" | "create" | "edit"
  const [mode, setMode] = useState("list")
  const [payments, setPayments] = useState([])
  const [selectedPayment, setSelectedPayment] = useState(null)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(null) // payment id being deleted

  // ── form state
  const [form, setForm] = useState({
      sr_rs: "",
      payment_mode: "cash",
      payment_date: new Date().toISOString().slice(0, 10),
      remarks: "",
  })

  // ── fetch payments list
  const fetchPayments = async () => {
    try {
      setLoading(true)
      const res = await externalEntityService.getShipmentPayments(shipmentId)
      setPayments(Array.isArray(res.data.payments) ? res.data.payments : [])
    } catch (err) {
      Alert.error(err.response?.data?.message || "Failed to load payments")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchPayments()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shipmentId])

  // ── helpers
  const resetForm = () =>
    setForm({
      sr_rs: "",
      payment_mode: "cash",
      payment_date: new Date().toISOString().slice(0, 10),
      remarks: "",
    })

  const openCreate = () => {
    resetForm()
    setSelectedPayment(null)
    setMode("create")
  }

  const openView = (p) => {
    setSelectedPayment(p)
    setMode("view")
  }

  const openEdit = (p) => {
    setSelectedPayment(p)
    setForm({
      sr_rs: p.payment_amount ?? "",
      payment_mode: p.payment_mode ?? "cash",
      payment_date: p.payment_date
        ? p.payment_date.slice(0, 10)
        : new Date().toISOString().slice(0, 10),
      remarks: p.remarks ?? "",
    })
    setMode("edit")
  }

  const backToList = () => {
    setMode("list")
    setSelectedPayment(null)
    resetForm()
  }

  // ── create payment
  const handleCreate = async () => {
    if (!form.sr_rs || isNaN(Number(form.sr_rs)) || Number(form.sr_rs) <= 0) {
      Alert.error("Please enter a valid SC Charge amount")
      return
    }
    try {
      setSaving(true)
      await externalEntityService.createShipmentPayment(shipmentId, {
        sr_rs: Number(form.sr_rs),
        payment_mode: form.payment_mode,
        payment_date: form.payment_date || null,
        remarks: form.remarks || null,
        created_by: user?.user_id,
        recorded_by_user_id: user?.user_id,
        recorded_by_name: user?.name,
      })
      Alert.success("Payment recorded successfully")
      await fetchPayments()
      onPaymentSaved?.()
      backToList()
    } catch (err) {
      Alert.error(err.response?.data?.message || "Failed to save payment")
    } finally {
      setSaving(false)
    }
  }

  // ── edit payment
  const handleEdit = async () => {
    try {
      setSaving(true)
      await externalEntityService.updateShipmentPayment(
        shipmentId,
        selectedPayment.id,
        {
          sr_rs: Number(form.sr_rs),
          payment_mode: form.payment_mode,
          payment_date: form.payment_date || null,
          remarks: form.remarks || null,
          updated_by: user?.user_id,
        }
      )
      Alert.success("Payment updated successfully")
      await fetchPayments()
      onPaymentSaved?.()
      backToList()
    } catch (err) {
      Alert.error(err.response?.data?.message || "Failed to update payment")
    } finally {
      setSaving(false)
    }
  }

  // ── delete payment
  const handleDelete = async (paymentId) => {
    if (!window.confirm("Delete this payment record? This cannot be undone."))
      return
    try {
      setDeleting(paymentId)
      await externalEntityService.deleteShipmentPayment(shipmentId, paymentId)
      Alert.success("Payment deleted")
      await fetchPayments()
      onPaymentSaved?.()
    } catch (err) {
      Alert.error(err.response?.data?.message || "Failed to delete payment")
    } finally {
      setDeleting(null)
    }
  }

  // ── payment mode badge
  const modeBadge = (mode) => {
    const map = {
      cash: "bg-green-100 text-green-800",
      online: "bg-blue-100 text-blue-800",
      cheque: "bg-purple-100 text-purple-800",
      to_pay: "bg-yellow-100 text-yellow-800",
      paid: "bg-green-100 text-green-800",
      paid_balance: "bg-blue-100 text-blue-800",
    }
    return map[(mode || "").toLowerCase()] || "bg-gray-100 text-gray-800"
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Render helpers
  // ─────────────────────────────────────────────────────────────────────────

  const FormFields = ({ readOnly = false }) => (
    <div className="space-y-4">
      {/* Amount — only editable on create */}
      <div>
        <label className="block text-xs font-semibold text-gray-600 mb-1">
          Payment Amount (₹) {!readOnly && mode === "create" && <span className="text-red-500">*</span>}
        </label>
        {readOnly || mode === "edit" ? (
          <div className="bg-gray-50 border border-gray-200 rounded-sm px-3 py-2 text-sm text-secondary font-semibold">
            ₹{(selectedPayment?.payment_amount || 0).toFixed(2)}
          </div>
        ) : (
          <input
            type="number"
            min="0"
            step="0.01"
            value={form.payment_amount}
            onChange={(e) => setForm({ ...form, payment_amount: e.target.value })}
            className="w-full border border-gray-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            placeholder="Enter amount"
          />
        )}
      </div>

      {/* Payment Mode */}
      <div>
        <label className="block text-xs font-semibold text-gray-600 mb-1">Payment Mode</label>
        {readOnly ? (
          <span className={`px-2 py-1 text-xs rounded ${modeBadge(selectedPayment?.payment_mode)}`}>
            {selectedPayment?.payment_mode || "—"}
          </span>
        ) : (
          <select
            value={form.payment_mode}
            onChange={(e) => setForm({ ...form, payment_mode: e.target.value })}
            className="w-full border border-gray-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
          >
            <option value="cash">Cash</option>
            <option value="online">Online</option>
            <option value="cheque">Cheque</option>
            <option value="to_pay">To Pay</option>
            <option value="paid">Paid</option>
            <option value="paid_balance">Paid Balance</option>
          </select>
        )}
      </div>

      {/* Payment Date */}
      <div>
        <label className="block text-xs font-semibold text-gray-600 mb-1">Payment Date</label>
        {readOnly ? (
          <div className="bg-gray-50 border border-gray-200 rounded-sm px-3 py-2 text-sm text-secondary">
            {selectedPayment?.payment_date
              ? new Date(selectedPayment.payment_date).toLocaleDateString("en-IN")
              : "—"}
          </div>
        ) : (
          <input
            type="date"
            value={form.payment_date}
            onChange={(e) => setForm({ ...form, payment_date: e.target.value })}
            className="w-full border border-gray-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
          />
        )}
      </div>

      {/* Remarks */}
      <div>
        <label className="block text-xs font-semibold text-gray-600 mb-1">Remarks</label>
        {readOnly ? (
          <div className="bg-gray-50 border border-gray-200 rounded-sm px-3 py-2 text-sm text-secondary min-h-[60px]">
            {selectedPayment?.remarks || "—"}
          </div>
        ) : (
          <textarea
            rows={3}
            value={form.remarks}
            onChange={(e) => setForm({ ...form, remarks: e.target.value })}
            className="w-full border border-gray-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 resize-none"
            placeholder="Optional remarks..."
          />
        )}
      </div>

      {/* Balance info — view only */}
      {readOnly && (
        <div className="grid grid-cols-2 gap-3 mt-2">
          <div className="bg-gray-50 border border-gray-200 rounded-sm p-3">
            <div className="text-xs text-gray-500 mb-1">Previous Balance</div>
            <div className="text-sm font-semibold text-secondary">
              ₹{(selectedPayment?.previous_balance || 0).toFixed(2)}
            </div>
          </div>
          <div className="bg-gray-50 border border-gray-200 rounded-sm p-3">
            <div className="text-xs text-gray-500 mb-1">New Balance</div>
            <div className="text-sm font-semibold text-blue-600">
              ₹{(selectedPayment?.new_balance || 0).toFixed(2)}
            </div>
          </div>
        </div>
      )}

      {/* Recorded by — view only */}
      {readOnly && selectedPayment?.recorded_by_name && (
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">Recorded By</label>
          <div className="bg-gray-50 border border-gray-200 rounded-sm px-3 py-2 text-sm text-secondary">
            {selectedPayment.recorded_by_name}
          </div>
        </div>
      )}
    </div>
  )

  // ─────────────────────────────────────────────────────────────────────────
  // Modal title per mode
  // ─────────────────────────────────────────────────────────────────────────
  const titles = {
    list: `Payment History — ${shipmentId}`,
    view: "View Payment",
    create: "Record New Payment",
    edit: "Edit Payment",
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-sm shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">

        {/* ── Header ── */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <div>
            <h2 className="text-base font-bold text-secondary">{titles[mode]}</h2>
            {mode === "list" && (
              <p className="text-xs text-gray-500 mt-0.5">
                LR: {shipment?.lr_no} · Balance: ₹{(shipment?.balance_amount || 0).toFixed(2)}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            {mode === "list" && (
              <button
                onClick={openCreate}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-sm transition-colors"
              >
                <Plus className="w-3.5 h-3.5" /> Add Payment
              </button>
            )}
            {mode !== "list" && (
              <button
                onClick={backToList}
                className="text-xs text-blue-500 hover:text-blue-700 font-semibold px-2 py-1 border border-blue-300 rounded-sm"
              >
                ← Back
              </button>
            )}
            <button
              onClick={onClose}
              className="p-1.5 hover:bg-gray-100 rounded-sm text-gray-500"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* ── Body ── */}
        <div className="flex-1 overflow-y-auto px-5 py-4">

          {/* ── LIST MODE ── */}
          {mode === "list" && (
            <>
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-4 border-b-4 border-brand"></div>
                </div>
              ) : payments.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-gray-400 font-semibold text-sm">No payment records found</p>
                  <button
                    onClick={openCreate}
                    className="mt-3 px-4 py-2 bg-blue-600 text-white text-xs font-semibold rounded-sm hover:bg-blue-700"
                  >
                    + Record First Payment
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  {payments.map((p, i) => (
                    <div
                      key={p.id}
                      className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-sm px-4 py-3"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-bold text-green-600">
                            ₹{(p.payment_amount || 0).toFixed(2)}
                          </span>
                          <span className={`px-2 py-0.5 text-xs rounded ${modeBadge(p.payment_mode)}`}>
                            {p.payment_mode || "—"}
                          </span>
                          <span className="text-xs text-gray-400">
                            {p.payment_date
                              ? new Date(p.payment_date).toLocaleDateString("en-IN")
                              : "—"}
                          </span>
                        </div>
                        <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                          <span>Prev: ₹{(p.previous_balance || 0).toFixed(2)}</span>
                          <span>→</span>
                          <span className="text-blue-600 font-semibold">
                            New: ₹{(p.new_balance || 0).toFixed(2)}
                          </span>
                          {p.recorded_by_name && <span>· {p.recorded_by_name}</span>}
                        </div>
                        {p.remarks && (
                          <p className="text-xs text-gray-400 mt-1 truncate">{p.remarks}</p>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-1 ml-3 shrink-0">
                        <button
                          onClick={() => openView(p)}
                          className="p-1.5 text-brand hover:bg-brand-light rounded-sm"
                          title="View"
                        >
                          <Eye className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => openEdit(p)}
                          className="p-1.5 text-blue-500 hover:bg-blue-50 rounded-sm"
                          title="Edit"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => handleDelete(p.id)}
                          disabled={deleting === p.id}
                          className="p-1.5 text-red-500 hover:bg-red-50 rounded-sm disabled:opacity-40"
                          title="Delete"
                        >
                          {deleting === p.id ? (
                            <div className="w-3.5 h-3.5 border-2 border-red-400 border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <Trash2 className="w-3.5 h-3.5" />
                          )}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {/* ── VIEW MODE ── */}
          {mode === "view" && <FormFields readOnly />}

          {/* ── CREATE / EDIT MODE ── */}
          {(mode === "create" || mode === "edit") && (
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">
                  SC Charge - sr_rs (₹) {mode === "create" && <span className="text-red-500">*</span>}
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.sr_rs}
                  onChange={(e) => setForm((f) => ({ ...f, sr_rs: e.target.value }))}
                  className="w-full border border-gray-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                  placeholder="Enter SC charge amount (adds to total & balance)"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Payment Mode</label>
                <select
                  value={form.payment_mode}
                  onChange={(e) => setForm((f) => ({ ...f, payment_mode: e.target.value }))}
                  className="w-full border border-gray-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                >
                  <option value="cash">Cash</option>
                  <option value="online">Online</option>
                  <option value="cheque">Cheque</option>
                  <option value="to_pay">To Pay</option>
                  <option value="paid">Paid</option>
                  <option value="paid_balance">Paid Balance</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Payment Date</label>
                <input
                  type="date"
                  value={form.payment_date}
                  onChange={(e) => setForm((f) => ({ ...f, payment_date: e.target.value }))}
                  className="w-full border border-gray-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Remarks</label>
                <textarea
                  rows={3}
                  value={form.remarks}
                  onChange={(e) => setForm((f) => ({ ...f, remarks: e.target.value }))}
                  className="w-full border border-gray-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 resize-none"
                  placeholder="Optional remarks..."
                />
              </div>
            </div>
          )}
        </div>

        {/* ── Footer (save/cancel for create & edit) ── */}
        {(mode === "create" || mode === "edit") && (
          <div className="px-5 py-4 border-t border-gray-200 flex justify-end gap-3">
            <button
              onClick={backToList}
              className="px-4 py-2 text-sm font-semibold text-gray-600 border border-gray-300 rounded-sm hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={mode === "create" ? handleCreate : handleEdit}
              disabled={saving}
              className="px-4 py-2 text-sm font-semibold bg-blue-600 text-white rounded-sm hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
            >
              {saving && (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              )}
              {mode === "create" ? "Save Payment" : "Update Payment"}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

export default ExternalShipmentPaymentModal